// src/components/CreateTransactionForm.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Grid,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Alert,
  Paper,
  Stack,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useSnackbar } from "notistack";
import { useAuth } from "../context/AuthContext";

const DEFAULT_CURRENCY = "USD";

const CreateTransactionForm = () => {
  const feeTouchedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const correction = location.state?.correction || null;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [form, setForm] = useState({
    sender_name: "",
    sender_phone: "",
    receiver_name: "",
    receiver_phone: "",
    secret_code: "",
    amount: "",
    currency: DEFAULT_CURRENCY,
    transaction_fee_percentage: "",
    sender_location_id: "",
    receiver_location_id: "",
    fee_included: false,
  });

  const [locations, setLocations] = useState([]);
  const [systemFeePercent, setSystemFeePercent] = useState(null);
  const [loading, setLoading] = useState(false);

  const assignedAgencyLabel = useMemo(() => {
    if (!user?.agency_id) return "";
    return [
      user?.agency_name || "",
      user?.city || user?.location_city || "",
      user?.country || user?.location_country || "",
    ]
      .filter(Boolean)
      .join(" • ");
  }, [user]);

  const money = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return `0.00 ${form.currency}`;

    return `${n.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${form.currency}`;
  };

  const sanitizePhone = (raw) => {
    if (!raw) return "";
    const trimmed = String(raw).trim();
    const hasPlus = trimmed.startsWith("+");
    const digitsOnly = trimmed.replace(/\D/g, "");
    return hasPlus ? `+${digitsOnly}` : digitsOnly;
  };

  const sanitizePercentInput = (raw) => {
    if (raw === "" || raw === null || raw === undefined) return "";
    let s = String(raw).replace(",", ".");
    s = s.replace(/[^0-9.]/g, "");
    const parts = s.split(".");
    if (parts.length > 2) s = `${parts[0]}.${parts.slice(1).join("")}`;
    return s;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === "sender_phone" || name === "receiver_phone") {
      setForm((prev) => ({ ...prev, [name]: sanitizePhone(value) }));
      return;
    }

    if (name === "transaction_fee_percentage") {
      feeTouchedRef.current = true;
      setForm((prev) => ({ ...prev, [name]: sanitizePercentInput(value) }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!correction) return;
    feeTouchedRef.current = true;
    setForm((prev) => ({
      ...prev,
      sender_name: correction.sender_name || "",
      sender_phone: correction.sender_phone || "",
      receiver_name: correction.receiver_name || "",
      receiver_phone: correction.receiver_phone || "",
      secret_code: "",
      amount: correction.amount != null ? String(correction.amount) : "",
      currency: correction.currency || DEFAULT_CURRENCY,
      transaction_fee_percentage:
        correction.transaction_fee_percentage != null
          ? String(correction.transaction_fee_percentage)
          : prev.transaction_fee_percentage,
      sender_location_id: correction.sender_location_id
        ? String(correction.sender_location_id)
        : prev.sender_location_id,
      receiver_location_id: correction.receiver_location_id
        ? String(correction.receiver_location_id)
        : "",
      fee_included: false,
    }));
  }, [correction]);

  useEffect(() => {
    const fetchInitData = async () => {
      try {
        const locRes = await api.get("/settings/locations");
        setLocations(Array.isArray(locRes.data) ? locRes.data : []);
      } catch (err) {
        console.error("Failed to load locations", err);
        enqueueSnackbar("Failed to load locations", { variant: "error" });
      }

      try {
        const res = await api.get("/settings/transaction-fee");

        const data = res?.data;
        const settingsObj =
          (Array.isArray(data) ? data[0] : null) ||
          data?.rows?.[0] ||
          data?.settings ||
          data;

        const candidate =
          settingsObj?.transaction_fee_percentage ??
          settingsObj?.default_transaction_fee_percentage ??
          settingsObj?.default_fee_percentage ??
          settingsObj?.fee_percentage;

        const n = Number(candidate);

        if (Number.isFinite(n) && n >= 0) {
          setSystemFeePercent(n);

          setForm((prev) => {
            if (feeTouchedRef.current) return prev;
            if (prev.transaction_fee_percentage !== "") return prev;
            return { ...prev, transaction_fee_percentage: String(n) };
          });
        } else {
          setSystemFeePercent(null);
          setForm((prev) => {
            if (feeTouchedRef.current) return prev;
            return { ...prev, transaction_fee_percentage: "" };
          });
        }
      } catch (err) {
        console.warn(
          "Failed to load default fee percentage from /settings/transaction-fee",
          err
        );
        setSystemFeePercent(null);
        setForm((prev) => {
          if (feeTouchedRef.current) return prev;
          return { ...prev, transaction_fee_percentage: "" };
        });
      }

      if (user?.location_id) {
        setForm((prev) => ({
          ...prev,
          sender_location_id: String(user.location_id),
        }));
      }
    };

    fetchInitData();
  }, [enqueueSnackbar, user]);

  const amountNumber = useMemo(() => {
    const n = parseFloat(form.amount);
    return Number.isFinite(n) ? n : 0;
  }, [form.amount]);

  const typedPercent = useMemo(() => {
    const raw = form.transaction_fee_percentage;
    if (raw === "" || raw === null || raw === undefined) return null;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  }, [form.transaction_fee_percentage]);

  const effectivePercent = useMemo(() => {
    if (typedPercent !== null) return typedPercent;
    if (systemFeePercent !== null) return systemFeePercent;
    return null;
  }, [typedPercent, systemFeePercent]);

  const canCalculateFee =
    effectivePercent !== null && effectivePercent >= 0 && amountNumber > 0;

  const feePreview = useMemo(() => {
    if (!canCalculateFee) {
      return {
        amountToSend: amountNumber,
        fee: 0,
        totalClientPays: amountNumber,
        clientGivenAmount: amountNumber,
      };
    }

    const percentRate = effectivePercent / 100;

    if (form.fee_included) {
      const amountToSend = amountNumber <= 100 + effectivePercent
        ? amountNumber - effectivePercent
        : amountNumber / (1 + percentRate);
      const fee = amountNumber - amountToSend;

      return {
        amountToSend,
        fee,
        totalClientPays: amountNumber,
        clientGivenAmount: amountNumber,
      };
    }

    const amountToSend = amountNumber;
    const fee = amountToSend <= 100 ? effectivePercent : amountToSend * percentRate;

    return {
      amountToSend,
      fee,
      totalClientPays: amountToSend + fee,
      clientGivenAmount: amountToSend + fee,
    };
  }, [amountNumber, canCalculateFee, effectivePercent, form.fee_included]);

  const amountLabel = form.fee_included
    ? "Montant donné par le client"
    : "Montant à envoyer";

  const validateFeePercentIfProvided = () => {
    if (
      form.transaction_fee_percentage === "" ||
      form.transaction_fee_percentage === null
    ) {
      return null;
    }

    const n = parseFloat(form.transaction_fee_percentage);
    if (!Number.isFinite(n)) return 'Le champ "Frais %" doit être un nombre valide.';
    if (n < 0) return "Le pourcentage ne peut pas être négatif.";
    if (n > 100) return "Le pourcentage ne peut pas dépasser 100%.";

    if (systemFeePercent !== null && n > systemFeePercent) {
      return `Le pourcentage des frais ne peut pas dépasser ${systemFeePercent}%.`;
    }

    return null;
  };

  const handleSubmit = async () => {
    const requiredFields = [
      "sender_name",
      "sender_phone",
      "receiver_name",
      "receiver_phone",
      "amount",
      "currency",
      "sender_location_id",
      "receiver_location_id",
    ];

    const fieldLabels = {
      sender_name: "Nom de l’expéditeur",
      sender_phone: "Téléphone de l’expéditeur",
      receiver_name: "Nom du bénéficiaire",
      receiver_phone: "Téléphone du bénéficiaire",
      amount: amountLabel,
      currency: "Devise",
      sender_location_id: "Provenance",
      receiver_location_id: "Destination",
    };

    for (const field of requiredFields) {
      if (!form[field]) {
        enqueueSnackbar(
          `Le champ "${fieldLabels[field] || field.replace(/_/g, " ")}" est obligatoire`,
          { variant: "warning" }
        );
        return;
      }
    }

    if (!user?.agency_id) {
      enqueueSnackbar("Votre compte n’a pas d’agence assignée", {
        variant: "error",
      });
      return;
    }

    const feeErr = validateFeePercentIfProvided();
    if (feeErr) {
      enqueueSnackbar(feeErr, { variant: "warning" });
      return;
    }

    if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
      enqueueSnackbar("Le montant doit être supérieur à 0", {
        variant: "warning",
      });
      return;
    }

    if (effectivePercent === null) {
      enqueueSnackbar("Impossible de calculer les frais. Veuillez saisir le pourcentage.", {
        variant: "warning",
      });
      return;
    }

    if (feePreview.amountToSend <= 0) {
      enqueueSnackbar("Le montant à envoyer doit être supérieur à 0", {
        variant: "warning",
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        sender_name: form.sender_name.trim(),
        sender_phone: form.sender_phone.trim(),
        receiver_name: form.receiver_name.trim(),
        receiver_phone: form.receiver_phone.trim(),
        amount: Number(feePreview.amountToSend.toFixed(2)),
        currency: DEFAULT_CURRENCY,
        secret_code: form.secret_code?.trim() || undefined,
        transaction_fee_percentage:
          form.transaction_fee_percentage !== ""
            ? parseFloat(form.transaction_fee_percentage)
            : undefined,
        sender_location_id: Number(form.sender_location_id),
        receiver_location_id: Number(form.receiver_location_id),
        correction_of_secret_code: correction?.secret_code || undefined,
        correction_reason: correction?.correction_reason || undefined,
      };

      const res = await api.post("/transactions", payload);
      const createdTx = res.data.transaction;

      enqueueSnackbar(
        correction ? "Transaction corrigée avec succès" : "Transaction créée avec succès",
        { variant: "success" }
      );

      const senderLocation = locations.find(
        (l) => String(l.id) === String(form.sender_location_id)
      );
      const receiverLocation = locations.find(
        (l) => String(l.id) === String(form.receiver_location_id)
      );

      feeTouchedRef.current = false;

      setForm({
        sender_name: "",
        sender_phone: "",
        receiver_name: "",
        receiver_phone: "",
        secret_code: "",
        amount: "",
        currency: DEFAULT_CURRENCY,
        transaction_fee_percentage:
          systemFeePercent !== null ? String(systemFeePercent) : "",
        sender_location_id: user?.location_id ? String(user.location_id) : "",
        receiver_location_id: "",
        fee_included: false,
      });

      navigate(`/transaction/${createdTx.secret_code}/receipt`, {
        state: {
          tx: {
            ...createdTx,
            sender_country: senderLocation?.country,
            sender_city: senderLocation?.city,
            receiver_country: receiverLocation?.country,
            receiver_city: receiverLocation?.city,
            fee_included: form.fee_included,
            client_given_amount: Number(feePreview.clientGivenAmount.toFixed(2)),
            amount_to_send: Number(feePreview.amountToSend.toFixed(2)),
            calculated_fee: Number(feePreview.fee.toFixed(2)),
            total_client_pays: Number(feePreview.totalClientPays.toFixed(2)),
          },
        },
      });
    } catch (err) {
      console.error("Failed to create transaction:", err);
      const msg = err?.response?.data?.error || "Transaction creation failed";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const feeHelperText = useMemo(() => {
    if (form.transaction_fee_percentage !== "") {
      return "Calcul basé sur la valeur saisie.";
    }
    if (systemFeePercent !== null) {
      return `Calcul basé sur le % par défaut: ${systemFeePercent}%.`;
    }
    return "Laisser vide pour utiliser le % par défaut du système.";
  }, [form.transaction_fee_percentage, systemFeePercent]);

  return (
    <Container maxWidth="sm">
      <Box mt={4}>
        <Typography variant="h5" gutterBottom>
          {correction ? "Corriger la transaction" : "Créer une nouvelle transaction"}
        </Typography>

        {correction && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            L’ancienne transaction <strong>{correction.secret_code}</strong> est annulée et reste
            inchangée dans l’historique. Vérifiez les informations puis créez la transaction
            corrigée : un <strong>nouveau code secret</strong> sera généré.
          </Alert>
        )}

        {user?.agency_id && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Agence assignée :</strong>{" "}
            {assignedAgencyLabel || `Agence #${user?.agency_id || "non définie"}`}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label={
                <span>
                  Nom de l'expéditeur <span style={{ color: "red" }}>*</span>
                </span>
              }
              name="sender_name"
              fullWidth
              value={form.sender_name}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={
                <span>
                  Numéro de l'expéditeur <span style={{ color: "red" }}>*</span>
                </span>
              }
              name="sender_phone"
              fullWidth
              value={form.sender_phone}
              onChange={handleChange}
              inputProps={{ inputMode: "numeric", pattern: "[0-9+]*", maxLength: 20 }}
              helperText="Chiffres uniquement ( + optionnel au début )."
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={
                <span>
                  Nom du destinataire <span style={{ color: "red" }}>*</span>
                </span>
              }
              name="receiver_name"
              fullWidth
              value={form.receiver_name}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={
                <span>
                  Numéro du destinataire <span style={{ color: "red" }}>*</span>
                </span>
              }
              name="receiver_phone"
              fullWidth
              value={form.receiver_phone}
              onChange={handleChange}
              inputProps={{ inputMode: "numeric", pattern: "[0-9+]*", maxLength: 20 }}
              helperText="Chiffres uniquement."
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Code secret (facultatif)"
              name="secret_code"
              fullWidth
              value={form.secret_code}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Frais % (facultatif)"
              name="transaction_fee_percentage"
              fullWidth
              value={form.transaction_fee_percentage}
              onChange={handleChange}
              inputProps={{ inputMode: "decimal" }}
              helperText={feeHelperText}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label={
                <span>
                  {amountLabel} <span style={{ color: "red" }}>*</span>
                </span>
              }
              name="amount"
              type="number"
              fullWidth
              value={form.amount}
              onChange={handleChange}
              inputProps={{ min: 0, step: "0.01" }}
              helperText={
                form.fee_included
                  ? "Exemple : le client donne 110000, commission incluse."
                  : "Exemple : le client veut envoyer 110000."
              }
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="currency-label">Devise</InputLabel>
              <Select
                labelId="currency-label"
                label="Devise"
                name="currency"
                value={form.currency}
                onChange={handleChange}
                disabled
              >
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.fee_included}
                  onChange={handleChange}
                  name="fee_included"
                />
              }
              label="Commission incluse dans le montant donné par le client"
            />
          </Grid>

          {amountNumber > 0 ? (
            <Grid item xs={12}>
              {effectivePercent === null ? (
                <Alert severity="warning">
                  Calcul indisponible : aucun pourcentage saisi et le pourcentage par défaut
                  n'a pas pu être chargé.
                </Alert>
              ) : (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Résumé financier
                    </Typography>

                    {form.fee_included ? (
                      <Alert severity="info">
                        Commission incluse : le montant envoyé est calculé avec la formule
                        montant donné ÷ (1 + frais%).
                      </Alert>
                    ) : (
                      <Alert severity="success">
                        Commission payée séparément : le bénéficiaire reçoit le montant saisi.
                      </Alert>
                    )}

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Montant à envoyer
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {money(feePreview.amountToSend)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Commission ({effectivePercent}%)
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {money(feePreview.fee)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total à donner par le client
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {money(feePreview.totalClientPays)}
                        </Typography>
                      </Grid>
                    </Grid>

                    {form.fee_included && (
                      <Typography variant="caption" color="text.secondary">
                        Vérification : {money(feePreview.amountToSend)} +{" "}
                        {money(feePreview.fee)} = {money(feePreview.totalClientPays)}.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}
            </Grid>
          ) : null}

          <Grid item xs={6}>
            <FormControl fullWidth disabled>
              <InputLabel id="sender-location-label" shrink>
                Provenance
              </InputLabel>
              <Select
                labelId="sender-location-label"
                id="sender-location"
                name="sender_location_id"
                value={form.sender_location_id}
                label="Provenance"
                displayEmpty
                onChange={handleChange}
              >
                <MenuItem value="" disabled>
                  <em>Sélectionner la provenance</em>
                </MenuItem>

                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={String(loc.id)}>
                    {loc.city}, {loc.country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="receiver-location-label" shrink>
                Destination
              </InputLabel>
              <Select
                labelId="receiver-location-label"
                id="receiver-location"
                name="receiver_location_id"
                value={form.receiver_location_id}
                label="Destination"
                displayEmpty
                onChange={handleChange}
              >
                <MenuItem value="" disabled>
                  <em>Sélectionner la destination</em>
                </MenuItem>

                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={String(loc.id)}>
                    {loc.city}, {loc.country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" fullWidth onClick={handleSubmit} disabled={loading}>
              {loading
                ? "Envoi en cours..."
                : correction
                ? "Créer la transaction corrigée"
                : "Créer la transaction"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default CreateTransactionForm;